export interface TestTypeDefinition {
  id: string
  discipline: 'run' | 'bike' | 'swim'
  name: string
  description: string
  fields: TestFieldDefinition[]
  calculatedFields?: CalculatedFieldDefinition[]
}

export interface TestFieldDefinition {
  key: string
  label: string
  type: 'time' | 'power' | 'heartRate'
  unit: string
  required?: boolean
  placeholder?: string
}

export interface CalculatedFieldDefinition {
  key: string
  label: string
  unit: string
  calculation: (data: any) => number | null
}

// Test type definitions
export const TEST_TYPES: TestTypeDefinition[] = [
  // Running tests
  {
    id: '5k',
    discipline: 'run',
    name: '5K Time Trial',
    description: '5 kilometer time trial',
    fields: [
      { key: 'time', label: 'Time', type: 'time', unit: 'MM:SS', required: true, placeholder: '20:00' },
      { key: 'avgHeartRate', label: 'Avg HR', type: 'heartRate', unit: 'bpm', placeholder: '165' },
    ],
  },
  {
    id: '10k',
    discipline: 'run',
    name: '10K Time Trial',
    description: '10 kilometer time trial',
    fields: [
      { key: 'time', label: 'Time', type: 'time', unit: 'MM:SS', required: true, placeholder: '42:00' },
      { key: 'avgHeartRate', label: 'Avg HR', type: 'heartRate', unit: 'bpm', placeholder: '160' },
    ],
  },

  // Swimming tests
  {
    id: 'css',
    discipline: 'swim',
    name: 'CSS Test',
    description: 'Critical Swim Speed test (400m + 200m)',
    fields: [
      { key: 'time400m', label: '400m Time', type: 'time', unit: 'MM:SS', required: true, placeholder: '5:30' },
      { key: 'time200m', label: '200m Time', type: 'time', unit: 'MM:SS', required: true, placeholder: '2:40' },
      { key: 'avgHeartRate', label: 'Avg HR', type: 'heartRate', unit: 'bpm', placeholder: '165' },
    ],
    calculatedFields: [
      {
        key: 'css',
        label: 'CSS',
        unit: 'm/s',
        calculation: (data) => {
          if (data.time400m && data.time200m) {
            // CSS = (400 - 200) / (T400 - T200) where times are in seconds
            // return distanceDiff / timeDiff; // m/s
            return (data.time400m - data.time200m) / 2
          }
          return null
        },
      },
    ],
  },

  // Cycling tests
  {
    id: 'ftp',
    discipline: 'bike',
    name: '20min FTP Test',
    description: '20-minute Functional Threshold Power test',
    fields: [
      { key: 'avgPower', label: '20min Avg Power', type: 'power', unit: 'watts', required: true, placeholder: '250' },
      { key: 'avgHeartRate', label: 'Avg HR', type: 'heartRate', unit: 'bpm', placeholder: '160' },
    ],
    calculatedFields: [
      {
        key: 'ftp',
        label: 'FTP',
        unit: 'watts',
        calculation: (data) => {
          if (data.avgPower) {
            // FTP is 95% of 20-minute average power
            return Math.round(data.avgPower * 0.95)
          }
          return null
        },
      },
    ],
  },
]

// Helper functions
export function getTestType(id: string): TestTypeDefinition | undefined {
  return TEST_TYPES.find((type) => type.id === id)
}

export function getTestTypesByDiscipline(discipline: 'run' | 'bike' | 'swim'): TestTypeDefinition[] {
  return TEST_TYPES.filter((type) => type.discipline === discipline)
}

export function calculateValues(testType: TestTypeDefinition, data: any): Record<string, number> {
  const calculated: Record<string, number> = {}

  if (testType.calculatedFields) {
    for (const field of testType.calculatedFields) {
      const value = field.calculation(data)
      if (value !== null) {
        calculated[field.key] = value
      }
    }
  }

  return calculated
}
